import { useRecoilValue } from 'recoil';
import { ActionsCell } from '../../Component/Common/Components/DataTable/ActionsCell';
import { DefaultCell } from '../../Component/Common/Components/DataTable/DefaultCell';
import { EE123PeopleRole_Edit, PeopleEditRole } from '../../Component/Common/Constants/UserRoles';
import { Individual } from "../../Component/Model/Dto/Individual";
import { TableColumn } from '../../Component/Model/Table/Table';
import { DateFormatter } from '../../Component/Model/helpers';
import { userRolesState } from '../../State/user.Atom';

interface IndividualTableRow {
    ID: number,
    IdentificationNumber: string,
    EmployeeCode: ICustomCell,
    FullName: ICustomCell,
    Race: ICustomCell,
    Gender: ICustomCell,
    Disabled: ICustomCell,
    CreatedDate: string,
    CreatedBy: string,
    ModifiedBy: string,
    Actions?: any,
    Style?: ICustomColor
}

interface ICustomColor {
    color: string;
    background: string;
}

interface ICustomCell {
    item: string;
    background: string;
}

export interface IIndividualTable {
    columns: Array<TableColumn>;
    rows: Array<IndividualTableRow>;
}

export function IndividualTableMapper(results: Individual.IndividualSummaryDto[], onEditPeopleIndividualRow: (id: number, scorcardId: string, isEditable: boolean) => void, onDeleteIndividualRow: (id: number) => void): IIndividualTable | undefined {

    const columns = [
        { Header: 'ID Number', accessor: 'IdentificationNumber', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Employee Code', accessor: 'EmployeeCode', id: "EmployeeCode", Cell: ({ value }: any) => <DefaultCell value={value.item} backgroundColour={value.background} />, disableSortBy: true },
        { Header: 'Scorecard Name', accessor: 'ScorecardName', id: 'ScorecardName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Scorecard Type', accessor: 'ScorecardType', id: 'ScorecardType', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Full Name', accessor: 'FullName', id: "FullName", Cell: ({ value }: any) => <DefaultCell value={value.item} backgroundColour={value.background} /> },
        { Header: 'Race', accessor: 'Race', id: "RaceId", Cell: ({ value }: any) => <DefaultCell value={value.item} backgroundColour={value.background} /> },
        { Header: 'Gender', accessor: 'Gender', id: "GenderId", Cell: ({ value }: any) => <DefaultCell value={value.item} backgroundColour={value.background} /> },
        { Header: 'Disabled', accessor: 'Disabled', id: "IsDisabled", Cell: ({ value }: any) => <DefaultCell value={value.item} backgroundColour={value.background} /> },
        { Header: 'Created Date', accessor: 'CreatedDate', id: "CreatedDateUtc", Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Created By', accessor: 'CreatedBy', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Modified By', accessor: 'ModifiedBy', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Actions', accessor: 'Actions', Cell: ({ value }: any) => <ActionsCell actions={value} />, disableSortBy: true },
    ];

    const editRoleAccess = [
        PeopleEditRole,
        EE123PeopleRole_Edit
    ]

    const userRoles = useRecoilValue(userRolesState);

    if (results.length >= 0) {

        let backgroundColors: Record<string, string> = {};
        let currentColor: string = '#ffffff';

        const rows = results.map((item: Individual.IndividualSummaryDto, index: number) => {
            if (!backgroundColors.hasOwnProperty(item.identificationNumber)) {
                backgroundColors[item.identificationNumber] = currentColor;
                currentColor = currentColor === '#ffffff' ? '#C1DCE0' : '#ffffff';
            }
            const discrepancyCells: string[] = [];

            if (index > 0) {
                const prevItem = results[index - 1];
                if (item.identificationNumber !== null) {
                    if (item.identificationNumber === prevItem.identificationNumber) {
                        if (item.employeeCode !== prevItem.employeeCode) {
                            discrepancyCells.push('employeeCode');
                        }
                        if (item.fullName !== prevItem.fullName) {
                            discrepancyCells.push('fullName');
                        }
                        if (item.race !== prevItem.race) {
                            discrepancyCells.push('race');
                        }
                        if (item.gender !== prevItem.gender) {
                            discrepancyCells.push('gender');
                        }
                        if (item.disabled !== prevItem.disabled) {
                            discrepancyCells.push('isDisabled');
                        }
                    }
                }
                else {
                    if (item.employeeCode === prevItem.employeeCode) {
                        if (item.fullName !== prevItem.fullName) {
                            discrepancyCells.push('fullName');
                        }
                        if (item.race !== prevItem.race) {
                            discrepancyCells.push('race');
                        }
                        if (item.gender !== prevItem.gender) {
                            discrepancyCells.push('gender');
                        }
                        if (item.disabled !== prevItem.disabled) {
                            discrepancyCells.push('isDisabled');
                        }
                    }
                }
            }

            const rowStyle: ICustomColor = { color: '', background: backgroundColors[item.identificationNumber] };
            if (discrepancyCells.length > 0) {
                rowStyle.color = '#EF8164';
            }
            const canEditEE = item.scorecardType === 'Employment Equity' && userRoles.some(r => r === EE123PeopleRole_Edit);
            const canEditBEE = item.scorecardType !== 'Employment Equity' && userRoles.some(r => r === PeopleEditRole);
            const isEditable = canEditEE || canEditBEE;
            return {
                ID: item.individualId,
                IdentificationNumber: item.identificationNumber,
                EmployeeCode: { item: item.employeeCode, background: discrepancyCells.includes('employeeCode') ? '#EF8164' : rowStyle.background },
                ScorecardName: item.scorecardName,
                ScorecardType: item.scorecardType,
                FullName: { item: item.fullName, background: discrepancyCells.includes('fullName') ? '#EF8164' : rowStyle.background },
                Race: { item: item.race, background: discrepancyCells.includes('race') ? '#EF8164' : rowStyle.background },
                Gender: { item: item.gender, background: discrepancyCells.includes('gender') ? '#EF8164' : rowStyle.background },
                Disabled: { item: item.disabled ? 'Yes' : 'No', background: discrepancyCells.includes('isDisabled') ? '#EF8164' : rowStyle.background },
                CreatedDate: DateFormatter(item.createdDate?.toString()),
                CreatedBy: item.createdBy ?? "Unspecified",
                ModifiedBy: item.modifiedBy ?? "Unspecified",
                Actions: [
                    isEditable ?
                        {
                            key: index.toString(),
                            label: 'Edit',
                            action: () => {
                                onEditPeopleIndividualRow(item.individualId, item.scorecardId, isEditable);
                            },
                            icon: {
                                color: 'info',
                                name: 'edit',
                            },
                        } :
                        {
                            key: index.toString(),
                            label: 'View',
                            action: () => {
                                onEditPeopleIndividualRow(item.individualId, item.scorecardId, isEditable);
                            },
                            icon: {
                                color: 'info',
                                name: 'visibility',
                            },
                        }
                    ,
                    isEditable
                        ? {
                            key: index.toString(),
                            label: 'Delete',
                            action: () => {
                                onDeleteIndividualRow(item.individualId);
                            },
                            icon: {
                                color: 'error',
                                name: 'delete',
                            },
                        }
                        : null,
                ].filter(Boolean),
                Style: { color: rowStyle.color, background: rowStyle.background }
            };
        });
        return { columns: columns, rows: rows };
    } else {
        return { columns: columns, rows: [] };
    }
}